/**
 * WorldTranslator - Handles world movement past a stationary camera
 *
 * CONCEPT:
 * Instead of moving the camera through the world, the camera stays at the origin
 * and the entire world moves past it. Objects are continuously generated ahead
 * and garbage collected behind.
 *
 * HOW IT WORKS:
 * 1. Camera and car stay at/near origin (0, height, 0)
 * 2. All world objects are children of worldGroup
 * 3. Each frame, worldGroup.position.z increases (moves toward camera)
 * 4. Objects behind camera are garbage collected
 * 5. New objects are generated ahead of camera
 *
 * FOR ARTISTS/DEVELOPERS:
 * Any object that exists "in the world" should be added using worldTranslator.add():
 *   - Road chunks, terrain, roadside objects
 *   - Birds, planes, clouds
 *   - Buildings, signs, vehicles, animals, etc.
 *
 * Objects that should NOT be added (stay with camera):
 *   - Sky sphere, star field, sun (atmospheric/infinite distance)
 *   - UI elements
 *   - Car model (child of camera)
 *   - Lights (typically follow camera)
 *
 * USAGE:
 *   // Add a world object
 *   const tree = new THREE.Mesh(geometry, material);
 *   tree.position.set(x, y, localZ);  // localZ is negative (ahead of camera)
 *   worldTranslator.add(tree);
 *
 *   // Check if object should be garbage collected
 *   if (worldTranslator.isPastCamera(object.position.z, 100)) {
 *       // Remove and dispose object
 *   }
 */

import * as THREE from 'three';

export class WorldTranslator {
    constructor(scene) {
        this.scene = scene;

        // The group containing all world objects
        // Moving this group moves everything in the world
        this.worldGroup = new THREE.Group();
        this.worldGroup.name = 'WorldGroup';
        scene.add(this.worldGroup);

        // Total distance traveled (for miles display and noise generation)
        // This accumulates forever
        this.totalDistanceTraveled = 0;
    }

    /**
     * Add an object to the world.
     * This is the PRIMARY method for adding any object that should
     * move with the world (terrain, objects, creatures, etc.)
     *
     * @param {THREE.Object3D} object - The object to add
     */
    add(object) {
        this.worldGroup.add(object);
    }

    /**
     * Remove an object from the world
     * @param {THREE.Object3D} object - The object to remove
     */
    remove(object) {
        this.worldGroup.remove(object);
    }

    /**
     * Check if an object is in the world group
     * @param {THREE.Object3D} object - The object to check
     * @returns {boolean}
     */
    contains(object) {
        return object.parent === this.worldGroup;
    }

    /**
     * Update world position based on current speed.
     * Called once per frame from main animation loop.
     *
     * @param {number} speed - Current speed in game units per second
     * @param {number} deltaTime - Time since last frame in seconds
     */
    update(speed, deltaTime) {
        const movement = speed * deltaTime;

        // Track total distance (grows forever, used for miles UI and noise)
        this.totalDistanceTraveled += movement;

        // Move world backward (positive Z = toward camera)
        this.worldGroup.position.z += movement;
    }

    /**
     * Get total distance traveled.
     * This grows forever and is used for UI and noise generation.
     * @returns {number} Total distance in world units
     */
    getTotalDistance() {
        return this.totalDistanceTraveled;
    }

    /**
     * Get the current position of the world group.
     * This is the offset applied to all world objects.
     *
     * @returns {number} World group Z position (positive, grows with travel)
     */
    getGroupOffset() {
        return this.worldGroup.position.z;
    }

    /**
     * Check if an object (by its local Z) has passed behind the camera.
     * The camera is at world Z=0. An object's visual position is:
     *   visualZ = localZ + worldGroup.position.z
     * If visualZ > cullDistance, the object is behind camera.
     *
     * @param {number} localZ - Object's local Z position
     * @param {number} cullDistance - How far behind before considered "passed"
     * @returns {boolean} True if object should be recycled/removed
     */
    isPastCamera(localZ, cullDistance = 100) {
        const visualZ = localZ + this.worldGroup.position.z;
        return visualZ > cullDistance;
    }

    /**
     * Get debug stats for monitoring
     * @returns {object} Debug statistics
     */
    getStats() {
        return {
            groupOffset: this.worldGroup.position.z,
            totalDistance: this.totalDistanceTraveled,
            childCount: this.worldGroup.children.length
        };
    }

    /**
     * Dispose of the world group and all children
     * Note: Does not dispose individual objects (managers handle their own disposal)
     */
    dispose() {
        if (this.worldGroup) {
            // Remove from scene
            this.scene.remove(this.worldGroup);

            // Clear all children (managers will have disposed their own objects)
            while (this.worldGroup.children.length > 0) {
                this.worldGroup.remove(this.worldGroup.children[0]);
            }

            this.worldGroup = null;
        }
        this.totalDistanceTraveled = 0;
    }

    /**
     * Reinitialize after disposal
     */
    reinitialize() {
        this.worldGroup = new THREE.Group();
        this.worldGroup.name = 'WorldGroup';
        this.scene.add(this.worldGroup);
        this.totalDistanceTraveled = 0;
    }
}
